"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { Sheet, SheetContent, SheetTitle } from "./sheet";

export interface BottomSheetTopArcProps {
  /** Height of the arc header section (default: 48) */
  arcHeight?: number | string;
  /** Fill color of the arc shape (default: "currentColor") */
  fillColor?: string;
  /** Border stroke color of the arc if applicable */
  strokeColor?: string;
  /** Stroke width if border stroke is enabled */
  strokeWidth?: number;
  /** Content placed at the top center peak of the half-circle arc */
  children?: React.ReactNode;
  /** Optional custom CSS classes for the arc wrapper container */
  className?: string;
  /** Curve style: "arch" for convex curve, "smooth" for bezier arc, or "half-circle" for half-circle dome cutout */
  variant?: "arch" | "smooth" | "half-circle";
}

export function BottomSheetTopArc({
  arcHeight = 0,
  fillColor = "currentColor",
  strokeColor,
  strokeWidth = 3,
  children,
  className,
  variant = "arch",
}: BottomSheetTopArcProps) {
  const heightStyle =
    typeof arcHeight === "number" ? `${arcHeight}px` : arcHeight;

  if (variant === "half-circle") {
    return (
      <div
        data-testid="bottom-sheet-top-arc-half-circle"
        className={cn(
          "relative flex w-full justify-center overflow-visible",
          className,
        )}
      >
        <div
          className="relative -mt-6 flex h-12 w-24 items-center justify-center rounded-t-full border-t-2 border-x-2 bg-white"
          style={{
            backgroundColor:
              fillColor !== "currentColor" ? fillColor : undefined,
            borderColor: strokeColor,
            borderWidth: strokeColor ? strokeWidth : undefined,
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="bottom-sheet-top-arc"
      className={cn("relative w-full overflow-hidden leading-none", className)}
      style={{ height: heightStyle }}
    >
      {children && (
        <div className="absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );
}

export interface TopArcBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  headerAction?: React.ReactNode;
  arcHeight?: number | string;
  arcColor?: string;
  arcVariant?: "arch" | "smooth" | "half-circle";
  arcContent?: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  desktopPlacement?: "center" | "bottom";
  closeButtonPosition?: "outside" | "inside";
  onPointerDownOutside?: (event: any) => void;
  onEscapeKeyDown?: (event: any) => void;
}

export function TopArcBottomSheet({
  isOpen,
  onClose,
  children,
  title,
  description,
  headerAction,
  arcHeight = 0,
  arcColor,
  arcVariant = "arch",
  arcContent,
  className,
  overlayClassName,
  contentClassName,
  desktopPlacement = "center",
  closeButtonPosition = "outside",
  onPointerDownOutside,
  onEscapeKeyDown,
}: TopArcBottomSheetProps) {
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const fallbackTitle = "Top arc bottom sheet dialog";

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        onPointerDownOutside={onPointerDownOutside}
        onEscapeKeyDown={onEscapeKeyDown}
        className={cn(
          "fixed! bottom-0! border-none bg-transparent p-0 shadow-none! focus:outline-none overflow-visible! md:fixed! md:left-1/2 md:right-auto! md:w-full md:max-w-md md:-translate-x-1/2",
          desktopPlacement === "bottom"
            ? "md:bottom-0! md:top-auto! md:translate-y-0"
            : "md:bottom-auto! md:top-1/2 md:-translate-y-1/2",
          contentClassName,
        )}
        overlayClassName={overlayClassName}
      >
        <div className="relative w-full overflow-visible">
          {headerAction && closeButtonPosition === "outside" && (
            <div className="absolute -top-2 right-2 z-50 flex items-center justify-end">
              {headerAction}
            </div>
          )}

          <div
            data-testid="top-arc-bottom-sheet-container"
            className={cn(
              "relative mx-auto w-full max-w-md max-h-[85vh] md:max-h-[90dvh] overflow-visible bg-white pb-6 pt-0 shadow-none!",
              "rounded-t-[32px]",
              desktopPlacement === "center" && "md:rounded-[32px]",
              className,
            )}
          >
            {/* Top Arc Half Circle Header */}
            <div className="relative w-full text-white">
              <BottomSheetTopArc
                arcHeight={arcHeight}
                fillColor={arcColor || "currentColor"}
                variant={arcVariant}
              >
                {arcContent}
              </BottomSheetTopArc>
            </div>

            <div className="">
              {title || (headerAction && closeButtonPosition === "inside") ? (
                <div className="flex items-center justify-between gap-3">
                  <SheetTitle
                    className={cn(
                      "text-lg font-semibold text-[#101010]",
                      !title && "sr-only",
                    )}
                  >
                    {title ?? fallbackTitle}
                  </SheetTitle>
                  {closeButtonPosition === "inside" && headerAction}
                </div>
              ) : (
                <SheetTitle className="sr-only">{fallbackTitle}</SheetTitle>
              )}

              {description && (
                <p className="mb-3 text-sm text-gray-600">{description}</p>
              )}

              {children}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Alias export for alternative naming
export { TopArcBottomSheet as ArcBottomSheet };
